import { useBoolean } from 'usehooks-ts';

import { DocumentActionItem } from '@/components/Form/ActionList/DocumentActionItem';

import { useDrawerOpenTracker } from '@/context';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import {
  selectBasicInfoStatus,
  selectUserData,
} from '@/redux/user-data/selectors';

import {
  DocumentSection,
  DocumentStatus,
  RequiredCondition,
} from '@/types/document';

import { getHigherStatus } from '@/utils/document';
import { isExistingCustomer } from '@/utils/user';

import { AdditionalIDSelectDrawer } from '../drawers/AdditionalID/AdditionalIDSelectDrawer';

export function AdditionalID() {
  const {
    value: isOpenAdditionalIDSelectDrawer,
    setTrue: openAdditionalIDSelectDrawer,
    setFalse: closeAdditionalIDSelectDrawer,
  } = useBoolean(false);

  useDrawerOpenTracker(isOpenAdditionalIDSelectDrawer);

  const { t } = useTranslate();
  const { user, requiredDocumentSections } = useAppSelector(selectUserData);

  const basicInfoStatus = useAppSelector(selectBasicInfoStatus);
  const isBasicInfoProcessed =
    basicInfoStatus === DocumentStatus.InReview ||
    basicInfoStatus === DocumentStatus.Approved ||
    basicInfoStatus === DocumentStatus.Rejected;

  const secondCitizenshipSection = requiredDocumentSections.find(
    section => String(section.type) === DocumentSection.SecondCitizenshipID,
  );

  const secondIdSection = requiredDocumentSections.find(
    section => section.type === DocumentSection.SecondID,
  );

  const additionalIdSections = [
    ...(secondIdSection ? [secondIdSection] : []),
    ...(secondCitizenshipSection ? [secondCitizenshipSection] : []),
  ];

  if (additionalIdSections.length === 0) return null;

  // Check all sections in additionalIdSections and get status
  // 1. If all sections are approved, return approved
  // 2. If any section is rejected, return rejected
  // 3. If any section is pending, return pending
  // 4. If all sections are in review, return in review
  const getSectionStatus = () => {
    return getHigherStatus(
      additionalIdSections
        .map(section => section?.status as DocumentStatus)
        .filter(Boolean),
      RequiredCondition.And,
    );
  };

  const additionalIdStatus = getSectionStatus();

  const isButtonDisabled = () => {
    if (!user) return true;
    return false;
  };

  return (
    <>
      <DocumentActionItem
        title={t('moreInfo.requiredDocumentGroup.secondIDHeader.highRisk')}
        description={t('status.pending')}
        onClick={openAdditionalIDSelectDrawer}
        status={additionalIdStatus}
        enabledForAllStatuses
        disabled={
          (user && isExistingCustomer(user) && !user.clientReviewed) ||
          !isBasicInfoProcessed
        }
        disabledButton={isButtonDisabled()}
      />
      <AdditionalIDSelectDrawer
        open={isOpenAdditionalIDSelectDrawer}
        sections={additionalIdSections}
        onClose={closeAdditionalIDSelectDrawer}
      />
    </>
  );
}
